<script setup lang="ts">
import { myRequest } from '@/utils/fetch'
import { ref } from 'vue'
import OGHeader from '@/components/OGHeader.vue'

const email = ref('')
const inviting = ref(false)
const referrals = ref<
  Array<{
    awarded: boolean
    awardedAt: string
    email: string
    createdAt: string
    firstName?: string
    lastName?: string
    picture?: string
    status?: string
    trialEnd?: string
    referralID: number
  }>
>()

const invite = async () => {
  inviting.value = true
  await myRequest('POST', '/referrals/invite', { email: email.value })
  getReferrals()
  inviting.value = false
}

const getReferrals = async () => {
  referrals.value = await myRequest('GET', '/referrals')
}

const getTrialDaysLeft = (trialEnd?: string) => {
  if (!trialEnd) return "Hasn't accepted the invite yet"
  const days = Math.ceil((new Date(trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  return days > 0 ? `Trial Period (${days} days left)` : 'Trial expired'
}

getReferrals()
</script>

<template>
  <OGHeader title="Referral Program" subtitle="Invite friends and earn rewards" />

  <div class="row q-col-gutter-md full-width">
    <div class="col-12 col-md paddingFixerSingle">
      <div class="myCard q-pa-md">
        <p class="text-h6 text-white">Invite a friend!</p>
        <div class="row q-pa-md bg-black rounded-borders" style="padding-bottom: 0">
          <q-input
            dense
            outlined
            v-model="email"
            :rules="[(val) => !!val || 'Email is required', 'email']"
            label="Enter your friend's email"
            class="col q-mr-md"
            bg-color="black"
            :disable="inviting"
          >
          </q-input>
          <q-btn
            style="height: 40px"
            class="col-2"
            @click="invite"
            label="Invite"
            color="primary"
            :loading="inviting"
            :disable="inviting"
          />
        </div>
        <div class="text-caption text-grey-6">
          When they sign up and convert to a paid user, you get a free month, on us!
        </div>
      </div>
    </div>

    <div class="col-12 col-md paddingFixerSingle">
      <div class="myCard q-pa-md full-height">
        <p class="text-h6 text-white">Free Months earned</p>
        <h4 class="text-primary">{{ referrals?.filter((r) => r.awarded).length }} months</h4>
        <div class="text-caption text-grey-6">
          Earned from {{ referrals?.filter((r) => r.awarded).length }} successful referrals
        </div>
      </div>
    </div>
  </div>

  <h6 v-if="referrals?.length" class="text-white q-py-md">Referrals</h6>

  <div class="row q-col-gutter-md full-width">
    <div
      class="col-12 col-sm-6 col-md paddingFixer4th"
      v-for="ref in referrals"
      :key="ref.referralID"
      style="min-width: 23%"
    >
      <div class="myCard q-pa-md">
        <div class="row">
          <q-avatar size="48" color="secondary">
            <img v-if="ref.picture" :src="ref.picture" />
            <q-icon v-if="!ref.firstName" name="mdi-account-tie" />
            <span v-else class="text-white">{{ ref.firstName.charAt(0).toUpperCase() }}</span>
          </q-avatar>
          <div class="q-pl-md col">
            <div class="text-h6 text-white text-truncate">
              {{ ref.firstName ? `${ref.firstName} ${ref.lastName}` : ref.email }}
            </div>
            <div :class="ref.awarded ? 'text-positive' : 'text-grey-6'">
              {{ ref.awarded ? 'Awarded' : getTrialDaysLeft(ref.trialEnd) }}
            </div>
          </div>
        </div>
        <div class="text-secondary text-bold q-pt-md">
          {{
            ref.awarded
              ? '+1 month'
              : getTrialDaysLeft(ref.trialEnd) === 'Trial expired'
                ? "Didn't subscribe"
                : 'Pending'
          }}
        </div>
      </div>
    </div>
  </div>
</template>
